import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { FeatureFlagDxpService } from '@pure1/data';
import { defer, Observable, of } from 'rxjs';
import { catchError, delay, switchMap, take, timeout } from 'rxjs/operators';

import { FeatureNames } from '../../model/FeatureNames';
import type { MockRoute } from './fixtures/existing-endpoints';
import { filterNamesMockAppliance } from './mock-request-filter';

const DATA_INSIGHTS_PATH_PREFIX = '/api/1.0/data-insights/';

/**
 * Endpoints outside /data-insights/ that the volume details card calls. The rest of the app calls them too,
 * so only a request whose filter names a mock appliance is even considered: every other one goes straight
 * to the backend without reading the flag. The fixtures then answer only for the mock volumes themselves.
 */
const MOCKABLE_PATHS_OUTSIDE_DATA_INSIGHTS: ReadonlySet<string> = new Set([
    '/rest/v3/volumes/capacity-metrics',
    '/rest/v1/dpa/fa-volumes',
]);

/** How long a request waits on the flag before going to the backend, should the lookup never settle. */
export const FLAG_TIMEOUT_MS = 2000;

/** The URL path of a request the mock might answer, or null for one it never answers. Never throws. */
function mockablePath(req: HttpRequest<unknown>): string | null {
    if (req.method !== 'GET') {
        return null;
    }
    try {
        const path = new URL(req.url, 'http://localhost').pathname;
        if (path.startsWith(DATA_INSIGHTS_PATH_PREFIX)) {
            return path;
        }
        return MOCKABLE_PATHS_OUTSIDE_DATA_INSIGHTS.has(path) && filterNamesMockAppliance(req) ? path : null;
    } catch {
        return null; // a URL the parser rejects is the backend's to answer
    }
}

function matches(route: MockRoute, path: string): boolean {
    return typeof route.path === 'string' ? route.path === path : route.path.test(path);
}

/** How long a mocked response takes, so loading states still show. Tests set it to 0. */
export const DATA_INTELLIGENCE_MOCK_DELAY_MS = new InjectionToken<number>('DATA_INTELLIGENCE_MOCK_DELAY_MS', {
    factory: () => 300,
});

// The fixtures are a lazy chunk: nobody downloads them unless the flag is on.
let mockRoutes: Promise<readonly MockRoute[]> | null = null;
function loadMockRoutes(): Promise<readonly MockRoute[]> {
    // One fixture module per endpoint family, their routes concatenated. To mock more endpoints, add a module here.
    mockRoutes ??= Promise.all([
        import('./fixtures/existing-endpoints').then(m => m.EXISTING_ENDPOINT_ROUTES),
        import('./fixtures/file-stats').then(m => m.FILE_STATS_ROUTES),
    ])
        .then(([existing, fileStats]) => [...existing, ...fileStats])
        .catch(err => {
            mockRoutes = null; // let the next request retry a failed chunk load
            throw err;
        });
    return mockRoutes;
}

/** Loads the mock route table. Swappable so specs can simulate a failed chunk load. */
export const DATA_INTELLIGENCE_MOCK_ROUTES_LOADER = new InjectionToken<() => Promise<readonly MockRoute[]>>(
    'DATA_INTELLIGENCE_MOCK_ROUTES_LOADER',
    { factory: () => loadMockRoutes },
);

/**
 * Serves canned Application Intelligence data for `/api/1.0/data-insights/` GETs (and the volume details
 * card's capacity and protection lookups for mock volumes) while the `data-intelligence-mock-data` flag is
 * on, so the pages have something to show on staging, where the real endpoints return nothing. Anything it
 * has no fixture for still goes to the backend.
 */
@Injectable()
export class DataIntelligenceMockInterceptor implements HttpInterceptor {
    private readonly featureFlags = inject(FeatureFlagDxpService);
    private readonly delayMs = inject(DATA_INTELLIGENCE_MOCK_DELAY_MS);
    private readonly loadRoutes = inject(DATA_INTELLIGENCE_MOCK_ROUTES_LOADER);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const path = mockablePath(req);
        if (path === null) {
            return next.handle(req);
        }

        return this.featureFlags.getFeatureFlag(FeatureNames.DATA_INTELLIGENCE_MOCK_DATA).pipe(
            // A flag lookup that never settles must not hold the request forever: treat it as off.
            timeout({ first: FLAG_TIMEOUT_MS, with: () => of(null) }),
            take(1),
            switchMap(flag => (flag?.enabled ? this.mockResponse(req, path, next) : next.handle(req))),
        );
    }

    private mockResponse(req: HttpRequest<unknown>, path: string, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return defer(() => this.loadRoutes()).pipe(
            // A failed chunk load must not take the real endpoints down with it: with no routes, every
            // request falls through to the backend below. Only the load is caught, never the backend's errors.
            catchError(() => of<readonly MockRoute[]>([])),
            switchMap(routes => {
                const body = this.mockBody(routes, path, req);
                if (body === undefined) {
                    return next.handle(req);
                }
                return of(new HttpResponse({ status: 200, body, url: req.urlWithParams })).pipe(delay(this.delayMs));
            }),
        );
    }

    /**
     * The body of the first matching route that has one, so overlapping routes each get their say. A fixture
     * that throws is a bug in the mock, not the backend's: the request goes to the backend as if unmocked.
     */
    private mockBody(routes: readonly MockRoute[], path: string, req: HttpRequest<unknown>): unknown {
        try {
            for (const route of routes) {
                const body = matches(route, path) ? route.respond(req) : undefined;
                if (body !== undefined) {
                    return body;
                }
            }
        } catch {
            // fall through to the backend
        }
        return undefined;
    }
}
